/**
 * 🐋 Cetalingua FM Admin Panel
 * 
 * Role-Based Routing:
 * - Admin → AdminDashboard
 * - Advertiser → AdvertiserDashboard (whale watching companies)
 * - Station → StationDashboard (placeholder)
 * - Station+Advertiser → Dashboard switcher
 *
 * STAGE 3 (29 Sep 2026): what a signed-in user sees now depends on what the account HAS (users/{uid}.products,
 * written only by the payment functions / admin grants), not only on the role. Admin is unchanged.
 * Everyone gets "Subscription" and "Plans". Stripe returns to /subscription after payment.
 */

import React, { useState, useEffect } from 'react';
import { auth } from './firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase-config';
import { getUserRole, getRoleDisplayName, getRoleIcon } from './utils/roleManager';

// Auth components
import Login from './components/Login';
import AdvertiserAuth from './components/AdvertiserAuth';
import AnalystLanding from './components/analyst/AnalystLanding';
import AnalysisWorkspace from './components/analyst/review/AnalysisWorkspace';
import { firestoreSource, myStationIds } from './components/analyst/review/reviewData';
import { getFunctions } from 'firebase/functions';

import TopBar from './components/common/TopBar';
import AboutPage from './components/common/AboutPage';

// Dashboards
import AdminDashboard from './components/AdminDashboard';
import AdvertiserDashboard from './components/advertiser/AdvertiserDashboard';
import StationDashboard from './components/station/StationDashboard';

// Advertiser Signup
import AdvertiserLanding from './components/advertiser/AdvertiserLanding';
import CheckoutPage from './components/advertiser/CheckoutPage';

// Station Signup
import StationLanding from './components/station/StationLanding';
import FmLanding from './components/fm/FmLanding'; // 15 Oct 2026

// Plans, payment, subscription (Stage 3)
import PortalPlans from './components/portal/PortalPlans';
import PortalSubscription from './components/portal/PortalSubscription';

// Legal Pages
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactPage from './pages/ContactPage';

import './App.css';
import portalIcon from './assets/portal-icon.png';
import fmIcon from './assets/fm-icon.png';
import CookieNotice from './components/common/CookieNotice';

// PREVIEW switch: `REACT_APP_ANALYSIS_DEMO=1 npm start` shows example calls; a normal build reads analysisItems and
// contains no example data (the lazy import below is dropped when the switch is off at build time).
const DemoWorkspace = process.env.REACT_APP_ANALYSIS_DEMO === '1'
  ? React.lazy(() => import('./components/analyst/review/DemoWorkspace')) : null;
let live = { key: '', source: null };
// one source per signed-in user and station list (so the review page does not reload on every profile update)
// researcher / admin reviews are final; decoder reviews wait for approval
// the tab label and the review rights come from this one function (user 8 Oct 2026: an account with isAdmin showed
// "Decode" but had the admin view)
function analysisRole(userData) {
  if (userData?.role === 'admin' || userData?.isAdmin === true) return 'admin';
  if (userData?.role === 'researcher' || (userData?.products || []).includes('researcher')) return 'researcher';
  return 'decoder';
}
function analysisSource(user, userData) {
  const key = `${user.uid}|${myStationIds(userData).join(',')}`;
  if (live.key !== key) live = { key, source: firestoreSource({ db, functions: getFunctions(), user, userData }) };
  return live.source;
}

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Auth choice: 'admin', 'advertiser', 'station'
  const [authChoice, setAuthChoice] = useState(null);
  
  // Navigation state for advertiser flow
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [previousPage, setPreviousPage] = useState('home');
  const [plansFrom, setPlansFrom] = useState('home');   // where "Back" on the plans page goes
  const [checkoutFrom, setCheckoutFrom] = useState(null); // where "Back" on the sign-up page goes
  
  // Which section a signed-in user sees: 'station' | 'advertiser' | 'analysis' | 'subscription' | 'plans'
  const [activeDashboard, setActiveDashboard] = useState(null);
  // users/{uid} live (products change when a payment is confirmed)
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (!user) { setUserData(null); return undefined; }
    return onSnapshot(doc(db, 'users', user.uid), (s) => {
      const d = s.data() || {};
      setUserData(d);
      if (d.role) setUserRole(d.role);                 // role is set by the server right after sign-up
    });
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log('🐋 User authenticated:', currentUser.email);
        const role = await getUserRole(currentUser.uid);
        console.log('🐋 User role:', role);
        setUserRole(role);
        setUser(currentUser);
        
        // Stripe sends the user back to /subscription after paying
        if (window.location.pathname.startsWith('/subscription')) setActiveDashboard('subscription');
      } else {
        console.log('🐋 No user authenticated');
        setUser(null);
        setUserRole(null);
        setActiveDashboard(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Navigation handlers
  const handleSelectPlan = (plan) => {
    console.log('Plan selected:', plan);
    setSelectedPlan(plan);
    setCheckoutFrom(currentPage);
    setCurrentPage('checkout');
  };

  const handleBackToLanding = () => {
    // Go back to the page the visitor came from (landing or plans)
    if (checkoutFrom && checkoutFrom !== 'checkout' && checkoutFrom !== 'home') {
      setCurrentPage(checkoutFrom);
    } else if (selectedPlan && typeof selectedPlan === 'object') {
      setCurrentPage('plans');
    } else if (selectedPlan === 'station' || selectedPlan === 'combined') {
      setCurrentPage('station-landing');
    } else {
      setCurrentPage('landing');
    }
    setSelectedPlan(null);
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
    setSelectedPlan(null);
    setAuthChoice(null);
    setPlansFrom('home');
  };

  const handleNavigateToLegal = (page) => {
    setPreviousPage(currentPage);
    if (page === 'login') {
      const fits = { 'station-landing': 'station', 'analyst-landing': 'analyst', plans: 'analyst' };
      setCurrentPage('home');
      setAuthChoice(fits[currentPage] || 'advertiser'); // the login that fits the page the visitor came from
    } else {
      setCurrentPage(page);
    }
  };

  const handleBackFromLegal = () => {
    setCurrentPage(previousPage || 'home');
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Cetalingua FM...</p>
      </div>
    );
  }

  // ========================================
  // LEGAL PAGES (Accessible from anywhere)
  // ========================================

  if (currentPage === 'terms') {
    return <TermsOfService onBack={handleBackFromLegal} />;
  }

  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={handleBackFromLegal} />;
  }

  if (currentPage === 'about') {
    return (
      <div>
        <TopBar onBack={handleBackFromLegal} backLabel="← Back" />
        <AboutPage />
      </div>
    );
  }

  if (currentPage === 'contact') {
    return <ContactPage onBack={handleBackFromLegal} />;
  }

  // ========================================
  // STATION FLOW (Before Authentication)
  // ========================================

  // 15 Oct 2026: "Listen to Cetalingua FM" — the listener app explained, with the store buttons
  if (currentPage === 'fm-landing' && !user) {
    return (
      <div>
        <TopBar onBack={handleBackToHome} />
        <FmLanding onNavigate={handleNavigateToLegal} onHostStation={() => setCurrentPage('station-landing')} />
      </div>
    );
  }

  if (currentPage === 'station-landing' && !user) {
    return (
      <div>
        <TopBar onBack={handleBackToHome} rightLabel="Sign in" onRight={() => handleNavigateToLegal('login')} />
        <StationLanding 
          onSelectPlan={handleSelectPlan} 
          onNavigate={handleNavigateToLegal}
        />
      </div>
    );
  }

  // ========================================
  // ADVERTISER FLOW (Before Authentication)
  // ========================================

  if (currentPage === 'landing' && !user) {
    return (
      <div>
        <TopBar onBack={handleBackToHome} rightLabel="Sign in" onRight={() => handleNavigateToLegal('login')} />
        <AdvertiserLanding 
          onSelectPlan={handleSelectPlan} 
          onNavigate={handleNavigateToLegal}
        />
      </div>
    );
  }

  if (currentPage === 'analyst-landing' && !user) {
    return (
      <div>
        <TopBar onBack={handleBackToHome} rightLabel="Sign in" onRight={() => handleNavigateToLegal('login')} />
        <AnalystLanding onSelectPlan={handleSelectPlan} onNavigate={handleNavigateToLegal}
          onAllPlans={() => { setPlansFrom('analyst-landing'); setCurrentPage('plans'); }} />
      </div>
    );
  }

  if (currentPage === 'plans' && !user) {
    return (
      <div>
        <TopBar onBack={plansFrom === 'analyst-landing' ? () => setCurrentPage('analyst-landing') : handleBackToHome}
          backLabel={plansFrom === 'analyst-landing' ? '← Back' : '← Back to Home'} />
        <PortalPlans user={null} userData={null}
          onNeedLogin={(sel) => { setSelectedPlan(sel); setCheckoutFrom('plans'); setCurrentPage('checkout'); }}
          onSticker={() => handleSelectPlan('free')} />
      </div>
    );
  }

  // stays on the sign-up page until the server has created the account (role) — then payment / dashboard
  // 18 Oct 2026: sign-ups closed until Stripe is live — every sign-up / plan button lands here instead of the
  // sign-up form. Re-open: REACT_APP_SIGNUPS_OPEN=1 in admin-panel/.env, then build + deploy.
  if (currentPage === 'checkout' && (!user || !userRole) && process.env.REACT_APP_SIGNUPS_OPEN !== '1') {
    return (
      <div>
        <TopBar onBack={handleBackToLanding} backLabel="← Back" />
        <div className="auth-choice-container">
          <div className="auth-choice-card" style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>🐋</div>
            <h1 style={{ marginBottom: 12 }}>Coming soon</h1>
            <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 20 }}>
              Sign-ups for stations, advertisers, decoders and researchers open very soon.
              Until then, have a look around — and get in touch if you would like to be among the first.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="partner-link" onClick={handleBackToLanding}>← Back</button>
              <button type="button" className="partner-link" onClick={() => handleNavigateToLegal('contact')}>✉️ Contact us</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentPage === 'checkout' && (!user || !userRole)) {
    return (
      <CheckoutPage 
        initialPlan={selectedPlan}
        onBack={handleBackToLanding}
        onNavigate={handleNavigateToLegal}
      />
    );
  }

  // ========================================
  // NO USER - SHOW LOGIN CHOICES
  // ========================================

  if (!user) {
    // Show login/signup choice screen
    if (!authChoice && currentPage === 'home') {
      return (
        <div className="auth-choice-container">
          <div className="auth-choice-card">
            <div className="home-head">
            <div className="auth-logo-container auth-logos">
              <figure>
                <img src={fmIcon} alt="Cetalingua FM" />
                <figcaption>Cetalingua FM app</figcaption>
              </figure>
              <figure>
                <img src={portalIcon} alt="Cetalingua Bioacoustic Portal" />
                <figcaption>Bioacoustic Portal</figcaption>
              </figure>
            </div>
            <h1 className="home-title">Cetalingua FM and Bioacoustic Portal</h1>
            </div>

            <div className="home-columns">
            <div className="home-col">
            <p className="home-col-title">Sign in — choose your account type</p>
            <div className="auth-choices">
              {/* Admin Login */}
              <button 
                className="choice-btn admin-btn"
                onClick={() => setAuthChoice('admin')}
              >
                <span className="choice-icon">🔐</span>
                <span className="choice-title">Admin</span>
                <span className="choice-desc">Manage approvals & content</span>
              </button>

              {/* Advertiser Login */}
              <button 
                className="choice-btn advertiser-btn"
                onClick={() => setAuthChoice('advertiser')}
              >
                <span className="choice-icon">🐋</span>
                <span className="choice-title">Advertiser / Whale Watching Company</span>
                <span className="choice-desc">Manage your listings & profile</span>
              </button>

              {/* Station Login */}
              <button 
                className="choice-btn station-btn"
                onClick={() => setAuthChoice('station')}
              >
                <span className="choice-icon">📡</span>
                <span className="choice-title">Station Operator</span>
                <span className="choice-desc">Manage your hydrophone station</span>
              </button>

              {/* Decoder / Researcher Login */}
              <button 
                className="choice-btn station-btn"
                onClick={() => setAuthChoice('analyst')}
              >
                <span className="choice-icon">🔬</span>
                <span className="choice-title">Decoder / Researcher</span>
                <span className="choice-desc">Analyse recordings, call types & results</span>
              </button>
            </div>
            </div>

            {/* New Partner CTAs */}
            <div className="new-partner-section home-col">
              <p className="home-col-title">New to Cetalingua FM?</p>
              <div className="partner-links">
                <button 
                  onClick={() => setCurrentPage('fm-landing')}
                  className="partner-link"
                >
                  🎧 Listen to Cetalingua FM
                </button>
                <button 
                  onClick={() => setCurrentPage('landing')}
                  className="partner-link advertiser-link"
                >
                  📢 Become an Advertiser / Whale Watching Company
                </button>
                <button 
                  onClick={() => setCurrentPage('station-landing')}
                  className="partner-link station-link"
                >
                  📡 Host a Station
                </button>
                <button 
                  onClick={() => setCurrentPage('analyst-landing')}
                  className="partner-link"
                >
                  🔬 Become a Decoder / Researcher
                </button>
              </div>
            </div>
            </div>

            {/* Footer Links */}
            <div className="home-footer-links">
              <button onClick={() => handleNavigateToLegal('terms')}>Terms of Service</button>
              <span>•</span>
              <button onClick={() => handleNavigateToLegal('privacy')}>Privacy Policy</button>
              <span>•</span>
              <button onClick={() => handleNavigateToLegal('contact')}>Contact Us</button>
              <span>•</span>
              <button onClick={() => handleNavigateToLegal('about')}>About</button>
            </div>
          </div>
        </div>
      );
    }

    // Admin login form
    if (authChoice === 'admin') {
      return (
        <div>
          <TopBar onBack={() => setAuthChoice(null)} backLabel="← Back" />
          <Login />
        </div>
      );
    }

    // Advertiser login form
    if (authChoice === 'advertiser') {
      return (
        <div>
          <TopBar onBack={() => setAuthChoice(null)} backLabel="← Back" />
          <AdvertiserAuth />
        </div>
      );
    }

    // Station login form (reuse admin login for now)
    if (authChoice === 'analyst') {
      return (
        <div>
          <TopBar onBack={() => setAuthChoice(null)} backLabel="← Back" />
          <Login title="Decoder / Researcher Login" />
        </div>
      );
    }

    if (authChoice === 'station') {
      return (
        <div>
          <TopBar onBack={() => setAuthChoice(null)} backLabel="← Back" />
          <Login title="Station Operator Login" />
        </div>
      );
    }
  }

  // ========================================
  // AUTHENTICATED USER ROUTING
  // ========================================

  // Admin → Admin Dashboard (app content, pushes, stations …) and, beside it, the Research workspace with the admin
  // view (library approvals, every station) — 9 Oct 2026: an admin login only reached the app dashboard
  if (userRole === 'admin') {
    const adminView = ['analysis', 'about'].includes(activeDashboard) ? activeDashboard : 'admin';
    return (
      <div className="combined-dashboard">
        <div className="dashboard-switcher">
          <button className={`switcher-btn ${adminView === 'admin' ? 'active' : ''}`} onClick={() => setActiveDashboard('admin')}>🛠️ App admin</button>
          <button className={`switcher-btn ${adminView === 'analysis' ? 'active' : ''}`} onClick={() => setActiveDashboard('analysis')}>🔬 Research (admin)</button>
          <button className={`switcher-btn ${adminView === 'about' ? 'active' : ''}`} onClick={() => setActiveDashboard('about')}>ℹ️ About</button>
        </div>
        {adminView === 'admin' && <AdminDashboard user={user} />}
        {adminView === 'about' && <AboutPage />}
        {adminView === 'analysis' && (userData
          ? <AnalysisWorkspace source={analysisSource(user, userData)} role="admin" />
          : <div className="loading-container"><div className="loading-spinner"></div><p>Loading your account...</p></div>)}
      </div>
    );
  }

  // Everyone else: sections by what the account has (products) — Stage 3
  if (userRole && !userData) {
    return (
      <div className="loading-container"><div className="loading-spinner"></div><p>Loading your account...</p></div>
    );
  }
  if (userRole) {
    const products = userData?.products || [];
    const sections = [];
    if (products.includes('station')) sections.push({ key: 'station', label: '📡 Station' });
    // advertisers keep their dashboard without a plan: the sticker program (proof photo) lives there
    if (products.includes('advertiser') || ['advertiser', 'station_advertiser'].includes(userRole)) {
      sections.push({ key: 'advertiser', label: '🐋 Advertiser' });
    }
    if (products.includes('decoder') || products.includes('researcher')) {
      sections.push({ key: 'analysis', label: { admin: '🔬 Research (admin)', researcher: '🔬 Research', decoder: '🔬 Decode' }[analysisRole(userData)] });
    }
    sections.push({ key: 'subscription', label: '💳 Subscription' });
    sections.push({ key: 'plans', label: '🏷️ Plans' });
    sections.push({ key: 'about', label: 'ℹ️ About' });
    const noPlan = !products.length;
    // no plan yet (e.g. right after sign-up without paying): start on Plans, except advertisers (sticker program)
    const fallback = noPlan && sections[0].key === 'subscription' ? 'plans' : sections[0].key;
    const current = sections.find((x) => x.key === activeDashboard)?.key || fallback;

    return (
      <div className="combined-dashboard">
        <div className="dashboard-switcher">
          {sections.map((x) => (
            <button key={x.key} className={`switcher-btn ${current === x.key ? 'active' : ''}`}
                    onClick={() => setActiveDashboard(x.key)}>
              {x.label}
            </button>
          ))}
          <button className="switcher-btn" onClick={() => auth.signOut()}>Sign out</button>
        </div>

        {noPlan && current === 'plans' && (
          <div style={{ maxWidth: 900, margin: '16px auto 0', padding: '12px 16px', background: 'rgba(0, 180, 216, 0.12)',
                        border: '1px solid var(--accent, #00b4d8)', borderRadius: 10, color: 'var(--text, #fff)' }}>
            Your account is ready. Choose a plan to start{['advertiser', 'station_advertiser'].includes(userRole)
              ? ' — or use the free sticker program in the Advertiser tab' : ''}.
          </div>
        )}

        {current === 'about' && <AboutPage />}
        {current === 'station' && <StationDashboard user={user} />}
        {current === 'advertiser' && <AdvertiserDashboard user={user} />}
        {current === 'analysis' && (
          DemoWorkspace
            ? <React.Suspense fallback={<p style={{ padding: 20 }}>Loading…</p>}><DemoWorkspace role={analysisRole(userData)} /></React.Suspense>
            : <AnalysisWorkspace source={analysisSource(user, userData)} role={analysisRole(userData)} />
        )}
        {current === 'subscription' && (
          <PortalSubscription user={user} onChoosePlan={() => setActiveDashboard('plans')} />
        )}
        {current === 'plans' && (
          <PortalPlans user={user} userData={userData}
            onManage={() => setActiveDashboard('subscription')}
            onSticker={() => setActiveDashboard('advertiser')} />
        )}
      </div>
    );
  }

  // No role or unknown role
  return (
    <div className="no-role-container">
      <div className="no-role-card">
        <span className="no-role-icon">⚠️</span>
        <h2>Account Setup Incomplete</h2>
        <p>Your account doesn't have a role assigned yet.</p>
        <p className="email-display">Logged in as: {user.email}</p>
        <p className="help-text">
          If you're a new advertiser, please complete the signup process.
          <br />
          For station operators, please contact support.
        </p>
        <div className="no-role-actions">
          <button 
            className="primary-btn"
            onClick={() => {
              auth.signOut();
              setCurrentPage('landing');
            }}
          >
            Complete Advertiser Signup
          </button>
          <button 
            className="secondary-btn"
            onClick={() => auth.signOut()}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

// 14 Oct 2026: cookie notice on every portal screen (shown once per browser)
export default function AppWithCookieNotice() {
  return (
    <>
      <App />
      <CookieNotice />
    </>
  );
}
